import React, {
  memo, useCallback, useEffect, useRef, useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Icon } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import fetchPendingAssignedInvoiceCount, {
  BILLIT_PENDING_ASSIGNED_REFRESH_EVENT,
} from '../../modules/billit-invoices-assigned/pendingAssignedInvoices';

const billitPurple = '#581A73';

const classes = mergeStyleSets({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    marginRight: 4,
    padding: '4px 10px 4px 8px',
    borderRadius: 999,
    background: 'rgba(255, 255, 255, 0.96)',
    border: '1px solid rgba(88, 26, 115, 0.28)',
    color: billitPurple,
    textDecoration: 'none',
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    boxShadow: '0 1px 4px rgba(88, 26, 115, 0.12)',
    transition: 'background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
    selectors: {
      ':hover': {
        background: '#fff',
        borderColor: 'rgba(88, 26, 115, 0.45)',
        boxShadow: '0 2px 8px rgba(88, 26, 115, 0.18)',
        textDecoration: 'none',
        color: billitPurple,
      },
      ':focus': {
        outline: `2px solid ${billitPurple}`,
        outlineOffset: 1,
      },
    },
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 20,
    height: 20,
    padding: '0 6px',
    borderRadius: 999,
    background: billitPurple,
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
  },
  label: {
    fontWeight: 600,
    color: billitPurple,
  },
  subLabel: {
    fontWeight: 400,
    color: 'rgba(88, 26, 115, 0.78)',
  },
});

const BillitAssignedInvoicesHeaderNotice = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [pendingCount, setPendingCount] = useState(0);
  const pendingCountRef = useRef(0);

  const loadCount = useCallback(async () => {
    if (!isAuthenticated) {
      if (pendingCountRef.current !== 0) {
        pendingCountRef.current = 0;
        setPendingCount(0);
      }
      return;
    }

    try {
      const count = await fetchPendingAssignedInvoiceCount(getAccessTokenSilently);
      if (pendingCountRef.current !== count) {
        pendingCountRef.current = count;
        setPendingCount(count);
      }
    } catch {
      if (pendingCountRef.current !== 0) {
        pendingCountRef.current = 0;
        setPendingCount(0);
      }
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    loadCount();

    const onRefresh = () => {
      loadCount();
    };

    window.addEventListener(BILLIT_PENDING_ASSIGNED_REFRESH_EVENT, onRefresh);

    return () => {
      window.removeEventListener(BILLIT_PENDING_ASSIGNED_REFRESH_EVENT, onRefresh);
    };
  }, [loadCount]);

  if (!isAuthenticated || pendingCount <= 0) {
    return null;
  }

  const invoiceLabel = pendingCount === 1 ? 'factuur' : 'facturen';

  return (
    <a
      href='/billit-invoices-assigned'
      className={classes.root}
      title={`${pendingCount} toegewezen ${invoiceLabel} wachten op beoordeling`}
    >
      <Icon iconName='Ringer' styles={{ root: { fontSize: 14, color: billitPurple } }} />
      <span className={classes.badge}>{pendingCount}</span>
      <span className={classes.label}>
        {pendingCount} {invoiceLabel}
        <span className={classes.subLabel}> · te beoordelen</span>
      </span>
    </a>
  );
};

export default memo(BillitAssignedInvoicesHeaderNotice);
